import React from 'react';
import {Link, withRouter} from 'react-router-dom';

import {translate} from 'iss/strings';
import {cancelEvent, eventStates, reActivateEvent, subscribeToEvent, updatePlannedDate} from 'iss/service/EvaluationEventService';
import dates from 'iss/utils/dates';
import {SubmitButton} from 'iss/widgets/Buttons';
import {Button} from 'iss/widgets/Buttons';
import {translateEvaluationType, translateEventAction, translateEventCancelReason, translateEventState} from 'iss/widgets/domainTranslations';
import {DownloadPdfButton} from '../widgets/DownloadBlobButton';
import {DatePicker, FormWithState, TextArea} from 'iss/widgets/Forms';
import {RestResultMessage} from 'iss/widgets/MessageBox';
import {EventCancelReasonSelector} from 'iss/widgets/Selectors';
import {formatParty} from 'iss/widgets/format/Party';
import {formatSiteAddress} from 'iss/widgets/format/Site';
import validators from 'iss/widgets/form/validation/validators';

const EvaluationEventEditor = withRouter(subscribeToEvent(class extends React.Component {
  state = {
    backendError: null,
  };
  getPlannedDate = () => dates.fromJson(this.props.event.plannedDate);
  updatePlannedDate = ({plannedDate}) => {
    let eventId = this.props.event.id;
    let event = {
      plannedDate: dates.formatUTC(plannedDate)
    };

    updatePlannedDate(eventId, event)
      .subscribe(
        () => this.props.history.push("/event/" + this.props.event.id),
        (errorResponse) => this.setState({backendError: errorResponse})
      );
  };
  cancelEvent = ({description, cancellingReason}) =>{
    cancelEvent(this.props.event.id, {reason: cancellingReason, description})
      .subscribe(
        () => this.props.history.push("/event/" + this.props.event.id),
        (errorResponse) => this.setState({backendError: errorResponse})
      );
  };
  reActivateEvent = ({description}) => {
    reActivateEvent(this.props.event.id, description)
      .subscribe(
        () => this.props.history.push("/event/" + this.props.event.id),
        (errorResponse) => this.setState({backendError: errorResponse})
      );
  };

  getLogRow = (logEvent, index) => {
    return <tr key={`event-${this.props.event.id}-log-${index}`} className="log-event">
      <td>{translateEventState(logEvent.data.eventState)}</td>
      <td>{dates.formatDateTime(logEvent.timestamp)}</td>
      <td>{this.getLogData(logEvent.data)}</td>
      <td>{logEvent.party ? formatParty(logEvent.party) : "-"}</td>
    </tr>;
  };

  getLogData = (data) => {
    let logData = "";
    if(data.cancelReason) {
      logData = translateEventCancelReason(data.cancelReason);
      if(data.cancelReasonDescription) {
        logData += ". " +data.cancelReasonDescription;
      }
    } else if(data.reactivateReasonDescription) {
      logData = data.reactivateReasonDescription;
    } else if(data.plannedDate) {
      logData = translate('eventEditor.plannedDate');
      logData += ": " + dates.format(data.plannedDate);
    }

    return logData;
  };

  getStateAwareTranslationForReactivation = (event) => {
    if (event.state === eventStates.finished) {
      return translate('eventEditor.reActivateEvent.from'
                        + eventStates.finished
                        + '.'
                        + event.evaluationType);
    } else {
      return translate('eventEditor.reActivateEvent.from' + event.state);
    }
  };

  render() {
    const {event} = this.props;
    const eventState = event.state;

    return <div>
      <h1>{translate('eventEditor.title')}</h1>
      <section id="event-info">
        <h2>{translate('eventEditor.eventInfo')}</h2>
        <table className="table table-striped">
          <tbody>
            <tr>
              <th>{translate('domain.evaluationEvent.eventId')}</th>
              <td>{event.id}</td>
            </tr>
            <tr>
              <th>{translate('domain.evaluationEvent.activationId')}</th>
              <td>{event.activationId}</td>
            </tr>
            <tr>
              <th>{translate('domain.activatedEvaluation.evaluationType')}</th>
              <td>{translateEvaluationType(event.evaluationType)}</td>
            </tr>
            <tr>
              <th>{translate('domain.evaluationEvent.site')}</th>
              <td>
                <Link to={"/activatedEvaluations/"+event.activationId}>{event.site.name} {formatSiteAddress(event.site)}</Link>
              </td>
            </tr>
            <tr>

              <th>{translate('domain.evaluationEvent.state')}</th>
              <td>{translateEventState(event.state)}</td>
            </tr>
            { event.isActivationActive &&
              ([eventStates.selfMonitoring, eventStates.readyForEvaluation, eventStates.started, eventStates.readyForCorrectionalRemarks].includes(eventState)) ?
                <tr>
                  <th>{translate('eventEditor.cancelEvent')}</th>
                  <td><CancelEventForm onCancelEvent={this.cancelEvent} /></td>
                </tr> : null
            }
            { event.isActivationActive && (
                eventState === eventStates.readyForCorrectionalRemarks || eventState === eventStates.readyForReview || eventState === eventStates.finished
              ) ?
                <tr>
                  <th>{this.getStateAwareTranslationForReactivation(event)}</th>
                  <td>
                    <ReactivateEventForm onSubmit={this.reActivateEvent} label={this.getStateAwareTranslationForReactivation(event)}/>
                  </td>
                </tr> : null
            }

            <tr>
              <th>{translate('eventEditor.plannedDate')}</th>
              <td>
                { event.isActivationActive && (eventState === eventStates.readyForEvaluation || eventState === eventStates.started) ?
                    <RescheduleEventForm eventDate={this.getPlannedDate()} onSubmit={this.updatePlannedDate} /> :
                    dates.format(event.plannedDate)
                }
              </td>
              <RestResultMessage id="backendError" result={this.state.backendError} />
            </tr>
            { event.isActivationActive && (
                eventState === eventStates.readyForEvaluation || eventState === eventStates.started || eventState === eventStates.readyForCorrectionalRemarks
              ) &&
              <tr>
                <th>{translateEventAction(eventState === eventStates.readyForCorrectionalRemarks ? 'fillCorrectionalRemarks' : 'fillEvaluation')}</th>
                <td>
                  <Button id='fill-evaluation' link={'/fill-evaluation/form/'+event.formId+'/event/'+event.id}>
                    {translateEventAction(eventState === eventStates.readyForCorrectionalRemarks ? 'fillCorrectionalRemarks' : 'fillEvaluation')}
                  </Button>
                </td>
              </tr>
            }
            { event.isActivationActive && eventState === eventStates.readyForReview &&
              <tr>
                <th>{translateEventAction('reviewCorrectiveActions')}</th>
                <td>
                  <Button id='review-evaluation' link={'/review-evaluation/form/'+event.formId+'/event/'+event.id}>
                    {translateEventAction('reviewCorrectiveActions')}
                  </Button>
                </td>
              </tr>
            }
            { event.isActivationActive && eventState === eventStates.finished &&
              <tr>
                <th>{translate('activationEditor.downloadPdfReport')}</th>
                <td>
                  <DownloadPdfButton url={`/event/${event.id}/report/pdf`} text={translate('activationEditor.downloadPdfReport')} />
                </td>
              </tr>
            }
          </tbody>
        </table>
      </section>

      <section>
        <h2>{translate('eventEditor.actions')}</h2>
        <table id="event-log" className="table table-striped">
          <thead>
            <tr>
              <th>{translate('eventEditor.state')}</th>
              <th>{translate('eventEditor.timestamp')}</th>
              <th>{translate('eventEditor.description')}</th>
              <th>{translate('eventEditor.editor')}</th>
            </tr>
          </thead>
          <tbody>
            {event.log ? event.log.map((logEvent, index) => this.getLogRow(logEvent, index)) : null}
          </tbody>
        </table>
      </section>
    </div>;
  }
}, () => <h1>{translate('eventEditor.eventNotFound')}</h1>));

function CancelEventForm({onCancelEvent}) {
  return <FormWithState id="cancel-event-form" onSubmit={onCancelEvent}>
    <EventCancelReasonSelector label="eventEditor.cancellingReason" valuePath="cancellingReason" validate={validators.chooseOne} id="cancelEvent" />
    <TextArea label="eventEditor.cancellingReasonDescription" valuePath="description" />
    <SubmitButton className="pull-left" id="cancel-event-button" buttonLook="danger" text={translate('eventEditor.cancelEvent')} />
  </FormWithState>;
}

function ReactivateEventForm({onSubmit, label}) {
  return <FormWithState id="reactivate-event-form" onSubmit={onSubmit}>
    <TextArea label="eventEditor.reActivationReason" valuePath="description" validate={validators.required} />
    <SubmitButton className="pull-left" id="reactivate-event-button" text={label} />
  </FormWithState>;
}

function RescheduleEventForm({eventDate, onSubmit}) {
  return <FormWithState id="reschedule-event-form" initialValues={{plannedDate: eventDate}}
    onSubmit={onSubmit}>
    <div className="input-group">
      <DatePicker id="event-date-picker" valuePath="plannedDate" validate={validators.required} />
      <SubmitButton className="pull-left" id="reschedule-event-button" text={translate('common.save')} />
    </div>
  </FormWithState>;
}


export default EvaluationEventEditor;



// WEBPACK FOOTER //
// ./modules/issFrontend/pages/EvaluationEventEditor.jsx